<template>
  <div class="students-view container py-4">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2 class="text-pink">Students Management</h2>

      <!-- Filters -->
      <div class="row g-3 mb-4">
        <div class="col-md-3">
          <select
            class="form-select"
            v-model="selectedGrade"
            @change="onGradeChange"
          >
            <option value="">Select Grade</option>
            <option v-for="g in grades" :key="g.id" :value="g.id">
              {{ g.level }}
            </option>
          </select>
        </div>
        <div class="col-md-6">
          <select
            class="form-select"
            v-model="selectedSubject"
            @change="onSubjectChange"
            :disabled="!selectedGrade"
          >
            <option value="">Select Subject</option>
            <option v-for="s in subjects" :key="s.id" :value="s.id">
              {{ s.name }}
            </option>
          </select>
        </div>
        <div class="col-md-3">
          <select
            class="form-select"
            v-model="selectedStatus"
            @change="fetchStudents(1)"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      <!-- Register Button triggers Bootstrap Modal -->
      <button
        type="button"
        class="btn btn-pink"
        data-bs-toggle="modal"
        data-bs-target="#registerStudentModal"
      >
        <i class="bi bi-plus-lg"></i> Register Student
      </button>
    </div>

    <!-- Bootstrap Modal for Registration -->
    <div
      class="modal fade"
      id="registerStudentModal"
      tabindex="-1"
      aria-labelledby="registerStudentModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog">
        <div class="modal-content bg-dark text-white border-pink">
          <div class="modal-header border-bottom-pink">
            <h5 class="modal-title" id="registerStudentModalLabel">
              Register New Student
            </h5>
            <button
              type="button"
              class="btn-close btn-close-white"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>

          <RegisterModel @student-added="onStudentAdded" />
        </div>
      </div>
    </div>

    <!-- Edit Modal -->
    <div
      class="modal fade"
      id="editStudentModal"
      tabindex="-1"
      aria-labelledby="editStudentModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog">
        <div class="modal-content bg-dark text-white border-pink">
          <div class="modal-header border-bottom-pink">
            <h5 class="modal-title" id="editStudentModalLabel">Edit Student</h5>
            <button
              type="button"
              class="btn-close btn-close-white"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>

          <EditStudent
            :student="selectedStudent"
            @student-edited="onStudentEdited"
          />
        </div>
      </div>
    </div>

    <p class="text-end text-pink mb-2">{{ pagination.total }} Students</p>

    <!-- Students Table -->
    <div class="table-responsive mt-3">
      <table class="table table-dark table-hover align-middle">
        <thead class="text-pink">
          <tr>
            <!-- <th>ID</th> -->
            <th>Name</th>
            <th>Email</th>
            <th>Phone</th>
            <th>Status</th>
            <th>Expiry Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <!-- Loading -->
          <tr v-if="loading">
            <td colspan="7" class="text-center py-4">
              <div class="spinner-border text-pink me-2" role="status"></div>
              Loading students...
            </td>
          </tr>

          <!-- No Students AFTER loading -->
          <tr v-else-if="!loading && students.length === 0">
            <td colspan="7" class="text-center py-4">
              <i class="bi bi-people"></i> No students available
            </td>
          </tr>

          <!-- Student Rows -->
          <tr v-else v-for="student in students" :key="student.id">
            <!-- <td>{{ student.id }}</td> -->
            <td>{{ student.first_name }} {{ student.last_name }}</td>
            <td>{{ student.email }}</td>
            <td>{{ student.phone }}</td>
            <td>
              <span :class="student.active ? 'text-success' : 'text-danger'">
                {{ student.active ? "Active" : "Inactive" }}
              </span>
            </td>
            <td>{{ student.expiry_date || "N/A" }}</td>
            <td>
              <!-- Edit Button -->
              <button
                type="button"
                @click="editStudent(student.id)"
                class="btn btn-sm btn-outline-warning me-1"
                data-bs-toggle="modal"
                data-bs-target="#editStudentModal"
              >
                <i class="bi bi-pencil-square"></i>
              </button>
              <button
                @click="deleteStudent(student.id)"
                class="btn btn-sm btn-outline-danger me-1"
              >
                <i class="bi bi-trash"></i>
              </button>
              <button
                @click="toggleStatus(student)"
                class="btn btn-sm btn-outline-info"
                :disabled="student.toggling"
              >
                <i
                  class="bi"
                  :class="student.active ? 'bi-person-dash' : 'bi-person-check'"
                ></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div class="d-flex justify-content-center mt-4">
        <nav class="Video pagination">
          <ul class="pagination custom-pagination">
            <!-- Previous -->
            <li class="page-item" :class="{ disabled: currentPage === 1 }">
              <button
                class="page-link"
                @click="fetchStudents(currentPage - 1)"
                :disabled="currentPage === 1"
              >
                Previous
              </button>
            </li>

            <!-- Page Numbers -->
            <li
              v-for="page in pagination.last_page"
              :key="page"
              class="page-item"
              :class="{ active: page === currentPage }"
            >
              <button class="page-link" @click="fetchStudents(page)">
                {{ page }}
              </button>
            </li>

            <!-- Next -->
            <li
              class="page-item"
              :class="{ disabled: currentPage === pagination.last_page }"
            >
              <button
                class="page-link"
                @click="fetchStudents(currentPage + 1)"
                :disabled="currentPage === pagination.last_page"
              >
                Next
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import RegisterModel from "./StudentParts/RegisterModel.vue";
import EditStudent from "./StudentParts/EditStudent.vue";

export default {
  name: "StudentsView",

  components: {
    RegisterModel,
    EditStudent,
  },

  data() {
    return {
      grades: [],
      subjects: [],
      students: [],
      pagination: {}, // 👈 add
      currentPage: 1, // 👈 add
      loading: false,
      selectedGrade: "",
      selectedSubject: "",
      selectedStatus: "",
      selectedStudent: null,
    };
  },

  methods: {
    async loadGrades() {
      try {
        const res = await axios.get("/a-get-grades");
        this.grades = res.data;
      } catch (err) {
        console.error(err);
      }
    },

    async loadSubjects(gradeId) {
      try {
        const res = await axios.get(`/a-grade-subjects?grade_id=${gradeId}`);
        this.subjects =
          res.data.subjects ||
          res.data.map((s) => ({ id: s.subject.id, name: s.subject.name }));
      } catch (err) {
        console.error(err);
        this.subjects = [];
      }
    },

    // async onGradeChange() {
    //   this.selectedSubject = "";
    //   await this.loadSubjects(this.selectedGrade);
    //   await this.fetchStudents();
    // },

    async onGradeChange() {
      this.selectedSubject = "";
      await this.loadSubjects(this.selectedGrade);
      await this.fetchStudents(1); // 👈 reset page
    },

    async onSubjectChange() {
      await this.fetchStudents(1);
    },

    // async onSubjectChange() {
    //   await this.fetchStudents();
    // },

    async fetchStudents(page = 1) {
      this.loading = true;

      try {
        const res = await axios.get("/get-students", {
          params: {
            page: page, // 👈 important
            grade_id: this.selectedGrade,
            subject_id: this.selectedSubject,
            status: this.selectedStatus,
          },
        });

        this.students = res.data.data; // 👈 important
        this.pagination = res.data; // 👈 save all pagination info
        this.currentPage = res.data.current_page;
      } catch (err) {
        console.error(err);
      } finally {
        this.loading = false;
      }
    },

    async onStudentAdded() {
      await this.fetchStudents(); // re-fetch fresh data
    },

    async onStudentEdited() {
      await this.fetchStudents(); // re-fetch fresh data
    },
    async editStudent(id) {
      try {
        const res = await axios.get(`/studentsbyId/${id}`);
        this.selectedStudent = res.data; // set selected student
      } catch (err) {
        console.error(err);
        alert("Failed to fetch student data");
      }
    },

    async deleteStudent(id) {
      if (!confirm("Are you sure?")) return;
      try {
        await axios.delete(`/delete-student/${id}`);
        this.students = this.students.filter((s) => s.id !== id);
      } catch (err) {
        console.error(err);
      }
    },

    async toggleStatus(student) {
      try {
        // Prevent multiple clicks
        student.toggling = true;

        // Call backend
        const res = await axios.patch(
          `/toggle-student-status/${student.id}/status`,
        );

        // Update status immediately
        student.active = res.data.active;
      } catch (err) {
        console.error(err);
        alert("Failed to toggle status");
      } finally {
        student.toggling = false;
      }
    },
  },

  mounted() {
    this.loadGrades();
    this.fetchStudents();
  },
};
</script>

<style scoped>
.text-pink {
  color: #ff00cc !important;
}
.btn-pink {
  background-color: #ff00cc;
  color: white;
  border: none;
}
.btn-pink:hover {
  background-color: #e600b3;
  color: white;
}
.border-pink {
  border-color: #ff00cc !important;
}
.border-bottom-pink {
  border-bottom: 2px solid #ff00cc !important;
}

/* Pagination Theme */
.custom-pagination .page-link {
  background-color: #1a1a1a;
  color: #ff00cc;
  border: 1px solid #333;
  transition: 0.3s;
}

.custom-pagination .page-link:hover {
  background-color: #ff00cc;
  color: #fff;
  border-color: #ff00cc;
}

/* Active Page */
.custom-pagination .page-item.active .page-link {
  background-color: #ff00cc;
  border-color: #ff00cc;
  color: white;
  font-weight: bold;
}

/* Disabled */
.custom-pagination .page-item.disabled .page-link {
  background-color: #111;
  color: #666;
  border-color: #222;
  cursor: not-allowed;
}
</style>
